import { FC, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { Box, Dialog, Typography } from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import LocalParkingIcon from '@mui/icons-material/LocalParkingOutlined'
import { colors } from '../../const/colors'
import { dateIso2localeString } from '../../utils/formatDate'
import { IParkingSubscription } from '../../graphql/users/queryUser'
import { IQueryBasicData } from '../../graphql/IQueryData'
import { useSnackbar } from '../Shared/useSnackbar'
import {
  CANCEL_PARKING_SUBSCRIPTION_FOR_USER,
  ICancelParkingSubscriptionForUserData,
  ICancelParkingSubscriptionForUserVars,
} from '../../graphql/parkingPool/cancelParkingSubscriptionForUser'
import {
  CANCEL_PARKING_POOL_RESERVATION_DAY,
  ICancelParkingPoolReservationDayData,
  ICancelParkingPoolReservationDayVars,
} from '../../graphql/parkingPool/cancelParkingPoolReservationDay'
import { CARD_SX, SectionHeader } from './UserDetailsUi'
import DayCheckbox from '../Parking/DayCheckbox'
import {
  DayEntry,
  daysOfSubscription,
  parkingsOfSubscription,
} from '../../utils/reservationDays'
import ReserveOnBehalfModal from './ReserveOnBehalfModal'

type CancelTarget =
  | { kind: 'subscription'; sub: IParkingSubscription }
  | { kind: 'day'; sub: IParkingSubscription; day: string; label: string }
  | { kind: 'days'; sub: IParkingSubscription; days: string[] }

const ReservationsSection: FC<{
  activeReservations: IParkingSubscription[]
  userName: string
  login: string
  parkingPoolId: string
  notify: (msg: string) => void
  onChanged: () => void
}> = ({
  activeReservations,
  userName,
  login,
  parkingPoolId,
  notify,
  onChanged,
}) => {
  const { t, i18n } = useTranslation([
    'user_details',
    'tenant',
    'btn',
    'generic',
  ])
  const { notifyError } = useSnackbar()
  const [reserveOpen, setReserveOpen] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null)
  const [expanded, setExpanded] = useState<string[]>([])
  const [selected, setSelected] = useState<Record<string, string[]>>({})
  const anchors = useRef<Record<string, number>>({})
  const [dropped, setDropped] = useState<Record<string, string[]>>({})

  const [cancelReservation, { loading: cancellingAll }] = useMutation<
    ICancelParkingSubscriptionForUserData,
    ICancelParkingSubscriptionForUserVars
  >(CANCEL_PARKING_SUBSCRIPTION_FOR_USER, { onError: notifyError })

  const [cancelDay, { loading: cancellingDay }] = useMutation<
    ICancelParkingPoolReservationDayData,
    ICancelParkingPoolReservationDayVars
  >(CANCEL_PARKING_POOL_RESERVATION_DAY, { onError: notifyError })

  const cancelling = cancellingAll || cancellingDay

  const toggleExpanded = (id: string) =>
    setExpanded(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    )

  const toggleDay = (
    subId: string,
    days: DayEntry[],
    index: number,
    shift: boolean,
  ) => {
    const target = days[index].day
    setSelected(prev => {
      const current = prev[subId] ?? []
      const anchor = anchors.current[subId]
      if (shift && anchor != null && anchor !== index) {
        const [from, to] = anchor < index ? [anchor, index] : [index, anchor]
        const range = days.slice(from, to + 1).map(entry => entry.day)
        return {
          ...prev,
          [subId]: current.includes(target)
            ? current.filter(day => !range.includes(day))
            : Array.from(new Set([...current, ...range])),
        }
      }
      return {
        ...prev,
        [subId]: current.includes(target)
          ? current.filter(day => day !== target)
          : [...current, target],
      }
    })
    anchors.current[subId] = index
  }

  const selectAllDays = (subId: string, days: DayEntry[]) =>
    setSelected(prev => ({ ...prev, [subId]: days.map(entry => entry.day) }))

  const clearSelection = (subId: string) =>
    setSelected(prev => ({ ...prev, [subId]: [] }))

  const finishCancel = (payload?: IQueryBasicData) => {
    setCancelTarget(null)
    if (payload?.status) {
      notify(t('generic:success_message'))
      onChanged()
    } else if (payload?.message) {
      notifyError(payload.message)
    }
  }

  const handleCancel = async () => {
    if (!cancelTarget) return
    if (cancelTarget.kind === 'day' || cancelTarget.kind === 'days') {
      const { sub } = cancelTarget
      const days =
        cancelTarget.kind === 'day' ? [cancelTarget.day] : cancelTarget.days
      const res = await cancelDay({
        variables: {
          parkingSubscription: sub.id,
          days,
          comment: t('cancel_default_comment'),
        },
      })
      const payload = res.data?.cancelParkingPoolReservationDay
      setCancelTarget(null)
      if (payload?.status) {
        setDropped(prev => ({
          ...prev,
          [sub.id]: [
            ...(prev[sub.id] ?? []),
            ...days.map(day => day.slice(0, 10)),
          ],
        }))
        clearSelection(sub.id)
        notify(
          days.length > 1
            ? t('cancel_days_done', { n: days.length })
            : t('generic:success_message'),
        )
        onChanged()
      } else if (payload?.message) {
        notifyError(payload.message)
      }
      return
    }
    const res = await cancelReservation({
      variables: {
        login,
        parkingSubscription: cancelTarget.sub.id,
        comment: t('cancel_default_comment'),
        notify: true,
      },
    })
    finishCancel(res.data?.cancelParkingSubscriptionForUser)
  }

  return (
    <>
      <Box sx={CARD_SX}>
        <SectionHeader
          icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
          title={t('reserve_on_behalf')}
          mb="6px"
        />
        <Typography
          sx={{
            fontSize: '12.5px',
            color: colors.textMuted,
            m: '0 0 16px',
            lineHeight: 1.5,
          }}
        >
          {t('reserve_on_behalf_info')}
        </Typography>
        <Box
          onClick={() => setReserveOpen(true)}
          data-e2e="reserve-on-behalf"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            height: 48,
            borderRadius: '13px',
            background: colors.greenMuted,
            color: colors.navy,
            fontWeight: 800,
            fontSize: '14px',
            cursor: 'pointer',
            boxShadow: colors.shadowKpiGreen,
            transition: 'background .15s',
            '&:hover': { background: '#9BBE9C' },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          {t('reserve')}
        </Box>
      </Box>

      <Box sx={CARD_SX}>
        <SectionHeader
          icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
          title={t('current_reservations')}
        />
        {activeReservations.length ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
            {activeReservations.map((sub, i) => {
              const parkings = parkingsOfSubscription(sub)
              const parkingName = parkings.length
                ? parkings.join(', ')
                : t('reservation_parking_unknown')
              const period = `${dateIso2localeString(
                sub.validFrom,
                i18n.language,
                {},
              )} – ${dateIso2localeString(sub.validTo, i18n.language, {})}`
              const days = daysOfSubscription(sub, dropped[sub.id])
              const isOpen = expanded.includes(sub.id)
              const picked = selected[sub.id] ?? []
              const allPicked = !!days.length && picked.length === days.length
              return (
                <Box
                  key={sub.id ?? i}
                  data-e2e="active-reservation"
                  sx={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: '13px',
                    p: '12px 14px',
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '14px',
                            fontWeight: 800,
                            color: parkings.length ? colors.navy : colors.slate,
                          }}
                        >
                          <LocalParkingIcon
                            sx={{ fontSize: 16, color: colors.slate }}
                          />
                          {parkingName}
                        </Typography>
                        <Box
                          component="span"
                          sx={{
                            background: colors.greenBg,
                            color: colors.greenBgText,
                            fontSize: '11.5px',
                            fontWeight: 700,
                            p: '4px 11px',
                            borderRadius: colors.radiusTag,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {t('reservation_active')}
                        </Box>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '12.5px',
                          color: colors.textMuted,
                          mt: '3px',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {period}
                      </Typography>
                    </Box>
                    <Box
                      onClick={() =>
                        setCancelTarget({ kind: 'subscription', sub })
                      }
                      data-e2e="cancel-whole"
                      sx={{
                        flexShrink: 0,
                        height: 38,
                        px: '16px',
                        borderRadius: '11px',
                        border: `1.5px solid ${colors.redBorder}`,
                        color: colors.red,
                        fontWeight: 700,
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'background .15s',
                        '&:hover': { background: colors.redBg },
                      }}
                    >
                      {t('cancel_whole')}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                      mt: '12px',
                      pt: '10px',
                      borderTop: `1px solid ${colors.border2}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: colors.slate,
                      }}
                    >
                      {t('reservation_days')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        flexShrink: 0,
                      }}
                    >
                      {days.length > 1 && isOpen && (
                        <Box
                          onClick={() =>
                            allPicked
                              ? clearSelection(sub.id)
                              : selectAllDays(sub.id, days)
                          }
                          data-e2e="select-all-days"
                          sx={{
                            fontSize: '12px',
                            fontWeight: 700,
                            color: colors.textSecondary,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            '&:hover': { color: colors.navy },
                          }}
                        >
                          {allPicked
                            ? t('unselect_all_days')
                            : t('select_all_days')}
                        </Box>
                      )}
                      {days.length > 1 && (
                        <Box
                          onClick={() => toggleExpanded(sub.id)}
                          data-e2e="toggle-days"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                            fontWeight: 700,
                            color: colors.textSecondary,
                            cursor: 'pointer',
                            '&:hover': { color: colors.navy },
                          }}
                        >
                          {isOpen ? (
                            <ExpandLessIcon sx={{ fontSize: 17 }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 17 }} />
                          )}
                          {isOpen ? t('hide_days') : t('show_days')}
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {(isOpen || days.length === 1) && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        mt: '8px',
                      }}
                    >
                      {!days.length && (
                        <Typography
                          sx={{ fontSize: '12.5px', color: colors.slate }}
                        >
                          {t('no_reservation_days')}
                        </Typography>
                      )}
                      {days.map((entry, dayIndex) => {
                        const isPicked = picked.includes(entry.day)
                        return (
                          <Box
                            key={entry.day}
                            onClick={event =>
                              toggleDay(sub.id, days, dayIndex, event.shiftKey)
                            }
                            data-e2e="reservation-day"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              p: '7px 4px 7px 10px',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              background: isPicked
                                ? colors.rowSelected
                                : colors.bg,
                              userSelect: 'none',
                              transition: 'background .12s',
                            }}
                          >
                            <DayCheckbox checked={isPicked} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  fontWeight: 700,
                                  color: colors.navy,
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {dateIso2localeString(
                                  entry.day,
                                  i18n.language,
                                  {},
                                )}
                              </Typography>
                              {!!entry.places.length && (
                                <Typography
                                  sx={{
                                    fontSize: '11.5px',
                                    color: colors.textMuted,
                                    fontWeight: 600,
                                  }}
                                >
                                  {entry.places.join(', ')}
                                </Typography>
                              )}
                            </Box>
                            <Box
                              data-e2e="cancel-day"
                              onClick={event => {
                                event.stopPropagation()
                                setCancelTarget({
                                  kind: 'day',
                                  sub,
                                  day: entry.day,
                                  label: dateIso2localeString(
                                    entry.day,
                                    i18n.language,
                                    {},
                                  ),
                                })
                              }}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                flexShrink: 0,
                                height: 32,
                                px: '12px',
                                borderRadius: '9px',
                                border: `1.5px solid ${colors.redBorder}`,
                                background: colors.surface,
                                color: colors.red,
                                fontSize: '12.5px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'background .15s',
                                '&:hover': { background: colors.redBg },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 15 }} />
                              {t('cancel_day')}
                            </Box>
                          </Box>
                        )
                      })}
                      {days.length > 1 && (
                        <Typography
                          sx={{
                            fontSize: '11.5px',
                            color: colors.slateLight,
                            fontWeight: 600,
                            pl: '10px',
                            pt: '2px',
                          }}
                        >
                          {t('select_days_hint')}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {!!picked.length && (
                    <Box
                      sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        mt: '10px',
                        mx: '-14px',
                        mb: '-12px',
                        p: '10px 14px',
                        borderTop: `1px solid ${colors.border2}`,
                        borderRadius: '0 0 13px 13px',
                        background: colors.surface,
                      }}
                    >
                      <Typography
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: '12.5px',
                          fontWeight: 700,
                          color: colors.navy,
                        }}
                      >
                        {t('selected_days_count', { n: picked.length })}
                      </Typography>
                      <Box
                        onClick={() => clearSelection(sub.id)}
                        sx={{
                          fontSize: '12.5px',
                          fontWeight: 700,
                          color: colors.textSecondary,
                          cursor: 'pointer',
                          flexShrink: 0,
                          '&:hover': { color: colors.navy },
                        }}
                      >
                        {t('clear_day_selection')}
                      </Box>
                      <Box
                        onClick={() =>
                          setCancelTarget({
                            kind: 'days',
                            sub,
                            days: [...picked].sort(),
                          })
                        }
                        data-e2e="cancel-selected-days"
                        sx={{
                          flexShrink: 0,
                          height: 36,
                          px: '14px',
                          borderRadius: '10px',
                          background: colors.red,
                          color: '#fff',
                          fontSize: '13px',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        {t('cancel_selected_days', { n: picked.length })}
                      </Box>
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: colors.bg,
              border: `1px solid ${colors.border}`,
              borderRadius: '13px',
              p: '14px 16px',
              fontSize: '13px',
              color: colors.slate,
              fontWeight: 600,
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 17 }} />
            {t('no_active_reservations')}
          </Box>
        )}
      </Box>

      <ReserveOnBehalfModal
        open={reserveOpen}
        onClose={() => setReserveOpen(false)}
        bookedRanges={activeReservations.map(sub => ({
          from: sub.validFrom.slice(0, 10),
          to: sub.validTo.slice(0, 10),
        }))}
        login={login}
        parkingPoolId={parkingPoolId}
        userName={userName}
        onConfirm={() => {
          setReserveOpen(false)
          notify(t('generic:success_message'))
          onChanged()
        }}
      />

      <Dialog
        open={!!cancelTarget}
        onClose={() => !cancelling && setCancelTarget(null)}
        PaperProps={{
          sx: {
            borderRadius: colors.radiusModal,
            width: 420,
            maxWidth: '100%',
            p: '26px',
          },
        }}
      >
        <Typography sx={{ fontSize: '19px', fontWeight: 800, mb: '10px' }}>
          {cancelTarget?.kind === 'day' && t('cancel_day_title')}
          {cancelTarget?.kind === 'days' && t('cancel_days_title')}
          {cancelTarget?.kind === 'subscription' &&
            t('cancel_reservation_title')}
        </Typography>
        <Typography
          sx={{
            fontSize: '13.5px',
            color: colors.textMuted,
            mb: cancelTarget?.kind === 'days' ? '12px' : '20px',
          }}
        >
          {cancelTarget?.kind === 'day'
            ? `${userName} · ${cancelTarget.label}`
            : cancelTarget?.kind === 'days'
              ? `${userName} · ${t('selected_days_count', {
                  n: cancelTarget.days.length,
                })}`
              : userName}
        </Typography>
        {cancelTarget?.kind === 'days' && (
          <Box
            sx={{
              mb: '20px',
              p: '10px 12px',
              maxHeight: 132,
              overflowY: 'auto',
              borderRadius: '11px',
              background: colors.bg,
              border: `1px solid ${colors.border}`,
              fontSize: '12.5px',
              fontWeight: 600,
              color: colors.textSecondary,
              lineHeight: 1.6,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {cancelTarget.days
              .map(day => dateIso2localeString(day, i18n.language, {}))
              .join(', ')}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <Box
            onClick={() => setCancelTarget(null)}
            sx={{
              flex: 1,
              height: 46,
              borderRadius: colors.radiusBtn,
              border: `1px solid ${colors.border3}`,
              color: colors.textSecondary,
              fontWeight: 700,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            {t('btn:back')}
          </Box>
          <Box
            onClick={() => !cancelling && handleCancel()}
            data-e2e="confirm-cancel"
            sx={{
              flex: 1.4,
              height: 46,
              borderRadius: colors.radiusBtn,
              background: colors.red,
              color: '#fff',
              fontWeight: 700,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: cancelling ? 'default' : 'pointer',
              opacity: cancelling ? 0.7 : 1,
            }}
          >
            {cancelTarget?.kind === 'day'
              ? t('cancel_day_confirm')
              : cancelTarget?.kind === 'days'
                ? t('cancel_selected_days', { n: cancelTarget.days.length })
                : t('cancel_reservation_confirm')}
          </Box>
        </Box>
      </Dialog>
    </>
  )
}

export default ReservationsSection
